<template>
  <div class="wrap">
    <header class="topbar"  :class="{ compact: !showMetricsPanel }">
      <button class="back" type="button" @click="back">
        {{ $t('facetool.common.back') }}
      </button>

      <div class="title">{{ $t('facetool.omni.title') }}</div>
      <div class="spacer" />

      <div class="mini">
        <button
          v-if="showFeatureBtn"
          class="iconBtn"
          type="button"
          @click="openFeatureModal"
        >
          {{ $t('facetool.omni.feature') }}
        </button>

        <div v-if="showMetaFaceType" class="metaSelectWrap">
          <select
            v-model="metaFaceType"
            class="metaSelect"
            @change="applyMetaFaceTypeToIframe"
            :aria-label="$t('facetool.omni.metaFaceType')"
          >
            <option v-for="t0 in META_FACE_TYPES" :key="t0" :value="t0">
              {{ t0 }}
            </option>
          </select>
        </div>

        <button
          ref="cameraBtn"
          v-if="showCameraBtn"
          class="iconBtn"
          :class="{ active: cameraPanelOpen }"
          type="button"
          @click="onCameraClick"
        >
          {{ $t('facetool.omni.camera') }}
        </button>

        <button class="iconBtn" type="button" @click="openFilePicker">
          {{ $t('facetool.omni.importPhoto') }}
        </button>

        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          class="hiddenFile"
          @change="onFileChange"
        />

        <input
          ref="ghostOverlayInput"
          type="file"
          accept="image/*"
          class="hidden-ghost-input"
          @change="onGhostFileChange"
        />

        <button
          v-if="currentMode === 'FacialAssessment'"
          class="iconBtn"
          type="button"
          :disabled="!lastExport"
          @click="exportZip"
        >
          匯出
        </button>


        <div v-if="cameraPanelOpen" ref="camPanel" class="camPanel" @click.stop>
          <div class="camRow">
            <div class="camLabel">{{ $t('facetool.omni.lens') }}</div>
            <select
              class="camSelect"
              v-model="selectedDeviceId"
              @change="onChangeDevice"
            >
              <option v-for="d in cameraDevices" :key="d.deviceId" :value="d.deviceId">
                {{ d.label }}
              </option>
            </select>
          </div>

          <div class="camRow">
            <label class="camCheck">
              <input type="checkbox" v-model="mirrorEnabled" @change="applyMirror" />
              {{ $t('facetool.omni.mirror') }}
            </label>
          </div>

          <div class="camRow camBtns">
            <button class="camBtn" type="button" @click="closeCameraPanel">
              {{ $t('facetool.common.close') }}
            </button>
          </div>
        </div>
      </div>
    </header>

    <teleport to="body">
      <div
        v-if="modeModalOpen"
        class="omni-modal-mask"
        role="dialog"
        aria-modal="true"
        :aria-label="$t('facetool.omni.switchMode')"
        @click.self="closeModeModal"
      >
        <div class="omni-modal">
          <div class="omni-modal-title">{{ $t('facetool.omni.switchMode') }}</div>

          <div class="omni-modal-btns">
            <button type="button" @click="setCurrentMode('FacialAssessment')">
              FacialAssessment
            </button>
            <button type="button" @click="setCurrentMode('FacialAgingScore')">
              FacialAgingScore
            </button>
            <button type="button" @click="setCurrentMode('facialAnalysis')">
              facialAnalysis
            </button>
          </div>

          <div class="omni-modal-foot">
            <button type="button" @click="closeModeModal">
              {{ $t('facetool.common.close') }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <teleport to="body">
      <div
        v-if="featureModalOpen"
        class="omni-modal-mask"
        role="dialog"
        aria-modal="true"
        :aria-label="$t('facetool.omni.feature')"
        @click.self="closeFeatureModal"
      >
        <div class="omni-modal">
          <div class="omni-modal-title">
            {{ $t('facetool.omni.featureWithMode', { mode: currentMode.value }) }}
          </div>

          <div v-if="currentMode === 'FacialAgingScore'" class="omni-modal-btns">
            <button type="button" @click="setFrMode('angle')">angle</button>
            <button type="button" @click="setFrMode('length')">length</button>
          </div>

          <div v-else-if="currentMode === 'facialAnalysis'" class="omni-modal-btns">
            <button
              v-for="m in AI_MODES"
              :key="m"
              type="button"
              @click="setAiMode(m)"
            >
              {{ m }}
            </button>
          </div>

          <div class="omni-modal-foot">
            <button type="button" @click="closeFeatureModal">
              {{ $t('facetool.common.close') }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <teleport to="body">
      <div
        v-if="gifModalOpen"
        class="omni-modal-mask"
        role="dialog"
        aria-modal="true"
        :aria-label="gifTitle"
        @click.self="closeGifModal"
      >
        <div class="omni-modal">
          <div class="omni-modal-title">{{ gifTitle }}</div>

          <div style="display:grid; place-items:center; padding: 6px 0 10px;">
            <img
              :src="gifSrc"
              :alt="gifTitle"
              style="max-width: 100%; max-height: 60vh; border-radius: 12px;"
            />
          </div>

          <div class="omni-modal-foot">
            <button type="button" @click="closeGifModal">
              {{ $t('facetool.common.close') }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <div class="content" :class="{ full: !showMetricsPanel }">
      <div class="frameWrap" :class="{ full: !showMetricsPanel }">
        <iframe
          ref="frame"
          class="frame"
          :src="src"
          allow="camera; microphone; autoplay; clipboard-read; clipboard-write"
          allowfullscreen
          @load="onFrameLoad"
        />

        <!-- 疊影顯示層已移至 iframe 內部處理，以確保與相機畫面比例一致 -->

        <div class="liveOverlay" aria-hidden="false">
          <button
            class="fabCapture"
            type="button"
            :class="{ danger: captureStatus === 'reset' }"
            @click="onCaptureToggle"
          >
            <span class="material-symbols-outlined camIcon" aria-hidden="true">
              photo_camera
            </span>
            <span class="btnText">
              {{ captureStatus === 'reset'
                ? $t('facetool.omni.retake')
                : $t('facetool.omni.capture')
              }}
            </span>
          </button>
        </div>

        <!-- 獨立於畫面左下方的直向排列控制按鈕 -->
        <div class="bottom-left-controls">
          <!-- 疊影 (Ghost Overlay) 按鈕 -->
          <button
            class="iconBtn stacked-btn ghost-btn"
            :class="{ active: ghostOverlayEnabled && ghostOverlaySrc }"
            type="button"
            @mousedown="handleGhostLongPressStart"
            @mouseup="handleGhostLongPressEnd"
            @mouseleave="handleGhostLongPressEnd"
            @touchstart="handleGhostLongPressStart"
            @touchend="handleGhostLongPressEnd"
            @click="handleGhostClick"
          >
            Ghost
          </button>

          <!-- fillMode 開關 (僅在 FacialAssessment 顯示) -->
          <button
            v-if="currentMode === 'FacialAssessment'"
            class="iconBtn stacked-btn"
            :class="{ active: fillModeEnabled }"
            type="button"
            @click="toggleFillMode"
          >
            fillMode
          </button>

          <!-- image-overlay 的五個開關 (各個模式保留) -->
          <div class="stacked-toggles">
            <button
              v-for="angle in ['L90', 'L45', '0', 'R45', 'R90']"
              :key="angle"
              class="iconBtn stacked-btn overlay-btn"
              :class="{ active: activeOverlay === angle }"
              type="button"
              @click="toggleOverlay(angle)"
            >
              {{ angle }}
            </button>
          </div>
        </div>
      </div>

      <section v-if="showMetricsPanel" class="panel">
        <div class="panelHead">
          <div class="panelTitle">{{ $t('facetool.omni.liveMetrics') }}</div>

          <div class="seg">
            <button
              class="segBtn"
              :class="{ active: viewTab === 'AU' }"
              type="button"
              @click="viewTab = 'AU'"
            >
              {{ $t('facetool.omni.actionUnit') }}
            </button>
            <button
              class="segBtn"
              :class="{ active: viewTab === 'Emotion' }"
              type="button"
              @click="viewTab = 'Emotion'"
            >
              {{ $t('facetool.omni.emotion') }}
            </button>
          </div>
        </div>

        <div v-if="liveFrame" class="echartsWrap">
          <div class="echartsScrollY">
            <div class="echartsInner" :style="{ height: chartHeightPx }">
              <div ref="chartEl" class="echarts"></div>
            </div>
          </div>
        </div>

        <div class="empty" v-else>
          {{ $t('facetool.omni.noData') }}
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import JSZip from 'jszip'

import * as echarts from 'echarts/core'
import { BarChart } from 'echarts/charts'
import { GridComponent, TooltipComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

import { processGhostImage } from '@/utils/ghostOverlay'



echarts.use([BarChart, GridComponent, TooltipComponent, CanvasRenderer])

const router = useRouter()
const route = useRoute()
const frame = ref(null)
const chartEl = ref(null)

const src = computed(() => '/omni/index.html')
function back () { router.push({ name: 'AppFaceTool' }) }

/** --------- receive live frames from iframe --------- */
const liveFrame = ref(null)
const viewTab = ref('AU')
const lastExport = ref(null)
// { capturedAt, imageDataUrl, metrics, modes }

let frameWin = null
let handler = null
let modeHandler = null

let pending = null
let rafId = 0

/** --------- Mode state (Vue side) --------- */
const currentMode = ref('FacialAssessment') // ✅ 預設
const frMode = ref('angle')                // ✅ AgingScore 預設
const aiMode = ref('aiMetaFace')           // ✅ facialAnalysis 預設

const AI_MODES = [
  'aiMetaFace','aiThree','aiFive','aiDeep','aiFace','aiJaw','aiFaceRect','aiEyebrow','aiNose'
]

const metaFaceType = ref('All')

const META_FACE_TYPES = [
  'All',
  'RU1','RU2','RU3',
  'RM1','RM2','RM3',
  'RL1','RL2','RL3',
  'LU1','LU2','LU3',
  'LM1','LM2','LM3',
  'LL1','LL2','LL3',
  'rEye','rNL','lEye','lNL'
]

const showMetaFaceType = computed(() =>
  currentMode.value === 'facialAnalysis' && aiMode.value === 'aiMetaFace'
)

const showMetricsPanel = computed(() => currentMode.value === 'FacialAssessment')
const showFeatureBtn = computed(() => currentMode.value !== 'FacialAssessment')
const showCameraBtn = computed(() => true) // 或根據需求調整顯示邏輯

/** --------- Modal state --------- */
const modeModalOpen = ref(false)
const featureModalOpen = ref(false)

/** --------- Image import (Vue -> iframe dispatchEvent('imageFromFlutter')) --------- */
const fileInput = ref(null)

const cameraPanelOpen = ref(false)
const cameraDevices = ref([])
const selectedDeviceId = ref('')
const mirrorEnabled = ref(true)
const cameraBtn = ref(null)
const camPanel = ref(null)

/** --------- Overlay toggles state --------- */
const fillModeEnabled = ref(true)
const activeOverlay = ref(null) // 'L90', 'L45', '0', 'R45', 'R90' or null

/** --------- Ghost Overlay state --------- */
const ghostOverlaySrc = ref(null)
const ghostOverlayEnabled = ref(false)
const ghostOverlayInput = ref(null)
let longPressTimer = null
let isLongPress = false

function handleGhostClick() {
  if (isLongPress) return // 如果是長按觸發的 click 則忽略
  
  if (!ghostOverlaySrc.value) {
    // 若無圖片，直接觸發上傳
    ghostOverlayInput.value?.click()
  } else {
    // 若有圖片，切換開關
    ghostOverlayEnabled.value = !ghostOverlayEnabled.value
    syncGhostOverlay()
  }
}

function syncGhostOverlay() {
  if (frameWin) {
    frameWin.dispatchEvent(new CustomEvent('omni:set-ghost-overlay', { 
      detail: { 
        dataUrl: ghostOverlaySrc.value, 
        enabled: ghostOverlayEnabled.value 
      } 
    }))
  }
}

function handleGhostLongPressStart() {
  isLongPress = false
  longPressTimer = setTimeout(() => {
    isLongPress = true
    ghostOverlayInput.value?.click()
  }, 600) // 長按 600ms 觸發重新上傳
}

function handleGhostLongPressEnd() {
  if (longPressTimer) {
    clearTimeout(longPressTimer)
    longPressTimer = null
  }
}

async function onGhostFileChange(e) {
  const file = e.target.files?.[0]
  if (!file) return

  const reader = new FileReader()
  reader.onload = async (event) => {
    const dataUrl = event.target.result
    try {
      const processed = await processGhostImage(dataUrl)
      ghostOverlaySrc.value = processed
      ghostOverlayEnabled.value = true
      syncGhostOverlay()
    } catch (err) {
      console.error('[GhostOverlay] Process failed:', err)
    }
  }
  reader.readAsDataURL(file)
  
  // 重置 input 以便下次選擇同一張圖
  e.target.value = ''
}

function toggleFillMode() {
  fillModeEnabled.value = !fillModeEnabled.value;
  if (frameWin && typeof frameWin.setFillMode === 'function') {
    callMaybe(frameWin.setFillMode, fillModeEnabled.value);
  } else if (frameWin) {
    // 透過 CustomEvent 傳遞 fallback
    frameWin.dispatchEvent(new CustomEvent('omni:set-fill-mode', { detail: { enabled: fillModeEnabled.value } }));
  }
}

function toggleOverlay(angle) {
  if (activeOverlay.value === angle) {
    activeOverlay.value = null; // 點擊已開啟的則關閉
  } else {
    activeOverlay.value = angle;
  }
  
  if (frameWin && typeof frameWin.setOverlayAngle === 'function') {
    callMaybe(frameWin.setOverlayAngle, activeOverlay.value);
  } else if (frameWin) {
    // 透過 CustomEvent 傳遞 fallback
    frameWin.dispatchEvent(new CustomEvent('omni:set-overlay', { detail: { angle: activeOverlay.value } }));
  }
}

/** --------- Capture state (from iframe) --------- */
const captureStatus = ref('start') // 'start' | 'stop' | 'reset'
const captureType = ref('Photo')   // 你目前 Vue UI 主要是 Photo

/** --------- GIF modal --------- */
const gifModalOpen = ref(false)
const gifTitle = ref('')
const gifSrc = ref('')

// ✅ webpack: 把該資料夾底下 .gif 全部納入打包
const gifMap = require.context('@/assets/gif/EN', false, /\.gif$/)

function resolveGif(name) {
  // name: "AU01" 或 "Happiness"...
  try {
    return gifMap(`./${name}.gif`)
  } catch (e) {
    console.warn('[OmniFaceTool] GIF not found:', name, e)
    return ''
  }
}

function auKeyToGifName(key) {
  const m = /^AU(\d+)(?:\((L|R)\))?$/.exec(String(key || ''))
  if (!m) return ''
  const n = Number(m[1])
  if (!Number.isFinite(n)) return ''
  return `AU${String(n).padStart(2, '0')}`
}

function emotionKeyToGifName(key) {
  const k = String(key || '')
  const allowed = new Set(['Anger','Disgust','Fear','Happiness','Sadness','Surprise'])
  return allowed.has(k) ? k : ''
}

function openGifModalByKey(key) {
  let name = ''
  if (/^AU\d+/.test(String(key))) name = auKeyToGifName(key)
  else name = emotionKeyToGifName(key)
  if (!name) return

  const src = resolveGif(name)
  if (!src) return

  gifTitle.value = name
  gifSrc.value = src
  gifModalOpen.value = true
}

function closeGifModal() {
  gifModalOpen.value = false
  gifTitle.value = ''
  gifSrc.value = ''
}


function syncCaptureState () {
  if (!frameWin) return
  try {
    const st = callMaybe(frameWin.getCaptureState) || {}
    if (st.captureStatus) captureStatus.value = st.captureStatus
    if (st.captureType) captureType.value = st.captureType
  } catch (e) {
    console.debug('[OmniFaceTool] syncCaptureState ignored:', e)
  }
}

async function ensurePhotoMode () {
  if (!frameWin) return
  // 你 Vue 目前主要是拍照，所以按拍攝前先確保是 Photo
  try {
    if (typeof frameWin.setCaptureType === 'function') {
      frameWin.setCaptureType('Photo')
    } else {
      // fallback：如果你 iframe 內還留著 radio，可以改成 click
      const d = frameWin.document
      d?.querySelector('input[name="captureType"][value="Photo"]')?.click()
    }
  } catch (e) {
    console.debug('[OmniFaceTool] ensurePhotoMode ignored:', e)
  }
}

async function onCaptureToggle () {
  if (!frameWin) return

  await ensurePhotoMode()

  const prev = captureStatus.value

  try {
    if (typeof frameWin.captureAction === 'function') {
      await frameWin.captureAction()
      syncCaptureState()

      // ✅ 若是 reset → start（使用者按了「重拍」）
      if (prev === 'reset' && captureStatus.value === 'start') {
        clearLastExport('retake')
        return
      }

      // ✅ 若是 start → reset（成功拍攝）
      await snapshotExportIfPhotoJustCaptured(prev)
      return
    }

    // fallback
    frameWin.document?.getElementById('photoBtn')?.click()
    syncCaptureState()

    if (prev === 'reset' && captureStatus.value === 'start') {
      clearLastExport('retake')
      return
    }

    await snapshotExportIfPhotoJustCaptured(prev)
  } catch (e) {
    console.error('[OmniFaceTool] onCaptureToggle failed:', e)
  }
}

function getIframeImageDataUrl() {
  try {
    // 你的 JS 拍照後會把 <img id="image"> 的 src 設成 dataURL
    const imgEl = frameWin?.document?.getElementById('image')
    const src = imgEl?.src || ''
    return (src.startsWith('data:image/')) ? src : ''
  } catch (e) {
    console.debug('[Export] getIframeImageDataUrl failed:', e)
    return ''
  }
}

function buildExportPayload() {
  const imageDataUrl = getIframeImageDataUrl()

  // metrics 來源：直接用你 Vue 現在的 liveFrame（就是 omni:live-frame 的 detail）
  // 這樣不用依賴 iframe 內部 results 結構，維護最輕
  const metrics = liveFrame.value ? JSON.parse(JSON.stringify(liveFrame.value)) : null

  return {
    capturedAt: new Date().toISOString(),
    imageDataUrl,
    metrics,
    modes: {
      currentMode: currentMode.value,
      frMode: frMode.value,
      aiMode: aiMode.value,
      metaFaceType: metaFaceType.value
    }
  }
}

async function snapshotExportIfPhotoJustCaptured(prevStatus) {
  // 只有「Photo：start -> reset」這一下代表拍到定格
  // 你的 iframe Photo 模式：start 點一次會變 reset
  // 所以 prevStatus = start，sync 後 captureStatus = reset => snapshot
  if (captureType.value !== 'Photo') return
  if (!(prevStatus === 'start' && captureStatus.value === 'reset')) return

  const payload = buildExportPayload()

  if (!payload.imageDataUrl) {
    console.warn('[Export] no imageDataUrl from iframe (maybe not in image mode yet)')
    // 有時候 image.src 還沒更新，等一幀再抓一次
    await new Promise(r => requestAnimationFrame(r))
    payload.imageDataUrl = getIframeImageDataUrl()
  }

  lastExport.value = payload
  console.log('[Export] snapshot saved:', lastExport.value)
}

async function exportZip() {
  // ✅ 只限定 FacialAssessment 可匯出
  if (currentMode.value !== 'FacialAssessment') return

  // ✅ 需要有資料（AU/Emotion 其實都在 liveFrame 裡）
  if (!liveFrame.value) {
    console.warn('[Export] no liveFrame yet')
    return
  }

  const ts = safeName(new Date().toISOString())
  const base = `omni_${ts}`
  const zip = new JSZip()

  // 1) 原圖（如果你也要）
  const imageDataUrl = getIframeImageDataUrl()
  if (imageDataUrl) {
    const photoBlob = dataUrlToBlob(imageDataUrl)
    const ext = photoBlob.type.includes('png') ? 'png' : 'jpg'
    zip.file(`${base}/photo.${ext}`, photoBlob)
  }

  // 2) AU 圖
  const auPng = await renderChartOffscreenToPng('AU')
  if (auPng) zip.file(`${base}/AU.png`, auPng)

  // 3) Emotion 圖
  const emoPng = await renderChartOffscreenToPng('Emotion')
  if (emoPng) zip.file(`${base}/Emotion.png`, emoPng)

  // 4) 簡化 json（只要 AU% / Emotion%）
  const au = normalizeToPct(toSeriesAU(liveFrame.value.auValues || []))
    .map(x => ({ key: x.key, pct: Number(x.pct.toFixed(2)) }))

  const emo = normalizeToPct(toSeriesEmotion(liveFrame.value.emotionValues || []))
    .map(x => ({ key: x.key, pct: Number(x.pct.toFixed(2)) }))

  zip.file(`${base}/metrics.json`, JSON.stringify({
    capturedAt: new Date().toISOString(),
    AU: au,
    Emotion: emo
  }, null, 2))

  const outBlob = await zip.generateAsync({ type: 'blob' })
  downloadBlob(outBlob, `${base}.zip`)

  // ✅ 保險：匯出後強制把 UI 那張 chart resize 一次（就算有人不小心動到也救得回來）
  requestAnimationFrame(() => { try { chart && chart.resize() } catch (_) {/* */} })
}



function safeName(s) {
  return String(s || '')
    .replaceAll(':', '-')
    .replaceAll('.', '-')
    .replaceAll(' ', '_')
}

function dataUrlToBlob(dataUrl) {
  const [meta, base64] = String(dataUrl || '').split(',')
  if (!base64) return new Blob([], { type: 'application/octet-stream' })

  const mime = /data:(.*?);base64/.exec(meta)?.[1] || 'application/octet-stream'
  const bin = atob(base64)
  const u8 = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i)
  return new Blob([u8], { type: mime })
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

function clearLastExport(reason = '') {
  if (lastExport.value) {
    console.log('[Export] cleared:', reason)
  }
  lastExport.value = null
}

function waitNextLiveFrame(timeoutMs = 1200) {
  return new Promise((resolve) => {
    const t = setTimeout(() => {
      stop()
      resolve(null)
    }, timeoutMs)

    const stop = watch(liveFrame, (v) => {
      if (!v) return
      clearTimeout(t)
      stop()
      resolve(v)
    }, { immediate: false })
  })
}

async function snapshotExportNow(reason = '') {
  const payload = buildExportPayload()

  // image 可能還沒寫進 iframe 的 <img id="image">，等一幀再抓一次
  if (!payload.imageDataUrl) {
    await new Promise(r => requestAnimationFrame(r))
    payload.imageDataUrl = getIframeImageDataUrl()
  }

  // 必須同時有 image + metrics 才有意義
  if (!payload.imageDataUrl || !payload.metrics) {
    console.warn('[Export] snapshot skipped (missing image/metrics)', { reason, payload })
    return
  }

  lastExport.value = payload
  console.log('[Export] snapshot saved:', reason, lastExport.value)
}


function isMobileUX () {
  // 觸控 + 小螢幕，基本上就是你要的「手機操作感」
  return (
    (navigator.maxTouchPoints && navigator.maxTouchPoints > 0) &&
    window.matchMedia('(max-width: 899px)').matches
  )
}

async function onCameraClick () {
  // ✅ 手機：直接切前後鏡頭，不開 panel
  if (isMobileUX()) {
    await toggleFrontBack()
    return
  }

  // ✅ 桌機：開 panel
  toggleCameraPanel()
}

function toggleCameraPanel () {
  cameraPanelOpen.value = !cameraPanelOpen.value

  if (cameraPanelOpen.value) {
    loadCameraList()

    // ✅ 避免「開啟那一下的 click」被 window listener 立刻接到又關閉
    setTimeout(() => {
      window.addEventListener('click', onOutsideClick, { capture: true })
    }, 0)
  } else {
    window.removeEventListener('click', onOutsideClick, { capture: true })
  }
}

function closeCameraPanel () {
  cameraPanelOpen.value = false
  window.removeEventListener('click', onOutsideClick, { capture: true })
}

function onOutsideClick (e) {
  // ✅ 點到相機按鈕或 panel 本體：不要關
  const btnEl = cameraBtn.value
  const panelEl = camPanel.value

  const t = e?.target
  if (btnEl && btnEl.contains(t)) return
  if (panelEl && panelEl.contains(t)) return

  closeCameraPanel()
}

async function loadCameraList () {
  if (!frameWin) return
  try {
    await callMaybe(frameWin.ensureCameraPermission)

    const list = await callMaybe(frameWin.getVideoInputDevices)
    cameraDevices.value = Array.isArray(list) ? list : []

    const st = callMaybe(frameWin.getCameraState) || {}
    mirrorEnabled.value = !!st.enableMirror

    // 優先用 iframe 回傳的 currentDeviceId，其次用第一個裝置
    selectedDeviceId.value =
      st.currentDeviceId ||
      cameraDevices.value?.[0]?.deviceId ||
      ''
  } catch (e) {
    console.error('[OmniFaceTool] loadCameraList failed:', e)
  }
}

async function onChangeDevice () {
  if (!frameWin || !selectedDeviceId.value) return
  try {
    await callMaybe(frameWin.switchCameraByDeviceId, selectedDeviceId.value)
  } catch (e) {
    console.error('[OmniFaceTool] switchCameraByDeviceId failed:', e)
  }
}

function applyMirror () {
  if (!frameWin) return
  try {
    callMaybe(frameWin.setMirrorEnabled, mirrorEnabled.value)
  } catch (e) {
    console.error('[OmniFaceTool] setMirrorEnabled failed:', e)
  }
}

async function toggleFrontBack () {
  if (!frameWin) return
  try {
    await callMaybe(frameWin.toggleFrontBackCamera)
    // 切完後同步一下狀態（特別是 mirror 可能跟著變）
    const st = callMaybe(frameWin.getCameraState) || {}
    mirrorEnabled.value = !!st.enableMirror
  } catch (e) {
    console.error('[OmniFaceTool] toggleFrontBackCamera failed:', e)
  }
}

function openFilePicker () {
  const el = fileInput.value
  if (!el) return
  // 讓同一張圖也能重選觸發 change
  el.value = ''
  el.click()
}

function onFileChange (e) {
  const input = e && e.target ? e.target : null
  const file = input && input.files && input.files[0] ? input.files[0] : null
  if (!file) return

  if (!file.type || !file.type.startsWith('image/')) {
    console.warn('[OmniFaceTool] Not an image:', file.type)
    return
  }

  const reader = new FileReader()
  reader.onload = () => {
    const dataUrl = String(reader.result || '')
    if (!dataUrl) return
    dispatchImageToIframe(dataUrl)
  }
  reader.onerror = (err) => {
    console.error('[OmniFaceTool] FileReader error:', err)
  }
  reader.readAsDataURL(file)
}

async function dispatchImageToIframe (dataUrl) {
  if (!frameWin) {
    console.warn('[OmniFaceTool] iframe window not ready yet.')
    return
  }

  try {
    // ✅ 1) 最穩：直接呼叫 camera.js 提供的 API
    if (typeof frameWin.loadImageForAnalysis === 'function') {
      await frameWin.loadImageForAnalysis(dataUrl)
      syncCaptureState()

      // ✅ 等分析結果回來（omni:live-frame）再建立匯出快照
      await waitNextLiveFrame(1500)
      await snapshotExportNow('import')
      
      return
    }

    // ✅ 2) fallback：丟你原本 Flutter 用的事件
    const evt = new CustomEvent('imageFromFlutter', {
      detail: { base64: dataUrl }
    })

    // 有些環境 window / document 監聽會不一樣，兩邊都丟一次更保險
    frameWin.dispatchEvent(evt)
    try {
      frameWin.document && frameWin.document.dispatchEvent(evt)
    } catch (e) {
      console.debug('[OmniFaceTool] dispatch to iframe.document ignored:', e)
    }
    
    console.warn('[OmniFaceTool] iframe has no loadImageForAnalysis; used CustomEvent fallback.')
  } catch (err) {
    console.error('[OmniFaceTool] dispatchImageToIframe failed:', err)
  }
}

//function openModeModal () { modeModalOpen.value = true }
function closeModeModal () { modeModalOpen.value = false }

function openFeatureModal () { featureModalOpen.value = true }
function closeFeatureModal () { featureModalOpen.value = false }

function applyMetaFaceTypeToIframe () {
  if (!frameWin) {
    console.warn('[OmniFaceTool] iframe window not ready yet.')
    return
  }

  try {
    // ✅ 你指定要改 window.metaFaceType
    frameWin.metaFaceType = metaFaceType.value

    // 兼容可能的命名（有就用）
    callMaybe(frameWin.setMetaFaceType, metaFaceType.value)
    callMaybe(frameWin.setMetaFaceTYPE, metaFaceType.value)
    callMaybe(frameWin.toggleMetaFaceType, metaFaceType.value)

    refreshIframeUI()
  } catch (e) {
    console.error('[OmniFaceTool] applyMetaFaceTypeToIframe failed:', e)
  }
}

/** ✅ ESC 關閉 + 鎖背景卷軸 */
function onKeydown (e) {
  if (e.key !== 'Escape') return
  if (featureModalOpen.value) closeFeatureModal()
  else if (modeModalOpen.value) closeModeModal()
}

watch([modeModalOpen, featureModalOpen], ([a, b]) => {
  const open = a || b
  document.body.style.overflow = open ? 'hidden' : ''
  if (open) window.addEventListener('keydown', onKeydown)
  else window.removeEventListener('keydown', onKeydown)
})

/** --------- iframe call helpers --------- */
function callMaybe(fn, ...args) {
  try {
    if (typeof fn === 'function') return fn(...args)
  } catch (e) {
    console.debug('[OmniFaceTool] callMaybe failed:', e)
  }
  return undefined
}

function refreshIframeUI() {
  if (!frameWin) return
  // 你在 camera.js 加的 applyModeUI：可能吃 payload / 單參數 / 無參數
  if (typeof frameWin.applyModeUI === 'function') {
    try {
      frameWin.applyModeUI({
        currentMode: currentMode.value,
        frMode: frMode.value,
        aiMode: aiMode.value
      })
      return
    } catch (e) {
      console.debug('[OmniFaceTool] applyModeUI(payload) ignored:', e)
    }

    try {
      frameWin.applyModeUI(currentMode.value)
      return
    } catch (e) {
      console.debug('[OmniFaceTool] applyModeUI(mode) ignored:', e)
    }

    try {
      frameWin.applyModeUI()
    } catch (e) {
      console.debug('[OmniFaceTool] applyModeUI() ignored:', e)
    }
  }
}

/** --------- 切 currentMode（主選單） --------- */
function setCurrentMode (mode) {
  currentMode.value = mode

  // UI 規則：切模式時把「功能」副選單關掉
  featureModalOpen.value = false

  // 切回 FacialAssessment 時，面板預設顯示 AU
  if (mode === 'FacialAssessment') viewTab.value = 'AU'

  if (!frameWin) {
    console.warn('[OmniFaceTool] iframe window not ready yet.')
    closeModeModal()
    return
  }

  try {
    // ✅ 你指定要改 window.currentMode
    frameWin.currentMode = mode

    // ✅ 也盡量呼叫你 iframe 內可能有的函式
    callMaybe(frameWin.setCurrentMode, mode)
    callMaybe(frameWin.toggleCurrentMode, mode)

    refreshIframeUI()
  } catch (e) {
    console.error('[OmniFaceTool] setCurrentMode failed:', e)
  } finally {
    closeModeModal()
  }
}

/** --------- 切 frMode（功能副選單 in AgingScore） --------- */
function setFrMode (m) {
  frMode.value = m

  if (!frameWin) {
    console.warn('[OmniFaceTool] iframe window not ready yet.')
    closeFeatureModal()
    return
  }

  try {
    // ✅ 你指定要改 window.frMode
    frameWin.frMode = m

    // 兼容一些可能的命名
    callMaybe(frameWin.setFrMode, m)
    callMaybe(frameWin.setFRMode, m)
    callMaybe(frameWin.toggleFrMode, m)

    refreshIframeUI()
  } catch (e) {
    console.error('[OmniFaceTool] setFrMode failed:', e)
  } finally {
    closeFeatureModal()
  }
}

/** --------- 切 aiMode（功能副選單 in facialAnalysis） --------- */
function setAiMode (m) {
  aiMode.value = m

  if (!frameWin) {
    console.warn('[OmniFaceTool] iframe window not ready yet.')
    closeFeatureModal()
    return
  }

  try {
    frameWin.aiMode = m

    callMaybe(frameWin.setAiMode, m)
    callMaybe(frameWin.setAIMode, m)
    callMaybe(frameWin.toggleAiMode, m)

    // ✅ 若切到 aiMetaFace：立刻把 metaFaceType 也套進去
    if (m === 'aiMetaFace') {
      frameWin.metaFaceType = metaFaceType.value
      callMaybe(frameWin.setMetaFaceType, metaFaceType.value)
    }

    refreshIframeUI()
  } catch (e) {
    console.error('[OmniFaceTool] setAiMode failed:', e)
  } finally {
    closeFeatureModal()
  }
}

function applyRoutePreset () {
  // query 都是字串（或 undefined）
  const q = route.query || {}
  const mode = typeof q.mode === 'string' ? q.mode : ''

  if (mode === 'FacialAssessment' || mode === 'FacialAgingScore' || mode === 'facialAnalysis') {
    // ✅ 先切主模式（會同步 iframe）
    setCurrentMode(mode)
  }

  // ✅ 子模式：只有對應模式才處理（避免亂套）
  if (mode === 'FacialAgingScore' && typeof q.frMode === 'string') {
    setFrMode(q.frMode)
  }

  if (mode === 'facialAnalysis') {
    if (typeof q.aiMode === 'string') setAiMode(q.aiMode)
    if (typeof q.metaFaceType === 'string') {
      metaFaceType.value = q.metaFaceType
      applyMetaFaceTypeToIframe()
    }
  }
}

/** --------- iframe load & live-frame listener --------- */
function onFrameLoad () {
  frameWin = frame.value?.contentWindow
  if (!frameWin) return

  // ----------------------------
  // 1) Sync state from iframe (or push defaults into iframe)
  // ----------------------------
  try {
    // mode
    const iframeMode =
      (typeof frameWin.currentMode === 'string' && frameWin.currentMode)
        ? frameWin.currentMode
        : ((typeof frameWin.mode === 'string' && frameWin.mode) ? frameWin.mode : '')

    if (iframeMode) {
      currentMode.value = iframeMode
    } else {
      frameWin.currentMode = currentMode.value
    }

    // frMode (你要控制 window.frMode)
    const iframeFrMode =
      (typeof frameWin.frMode === 'string' && frameWin.frMode)
        ? frameWin.frMode
        : ((typeof frameWin.currentFrMode === 'string' && frameWin.currentFrMode) ? frameWin.currentFrMode : '')

    if (iframeFrMode) {
      frMode.value = iframeFrMode
    } else {
      frameWin.frMode = frMode.value
    }

    // aiMode (你要控制 window.aiMode)
    const iframeAiMode =
      (typeof frameWin.aiMode === 'string' && frameWin.aiMode)
        ? frameWin.aiMode
        : ((typeof frameWin.currentAiMode === 'string' && frameWin.currentAiMode) ? frameWin.currentAiMode : '')

    if (iframeAiMode) {
      aiMode.value = iframeAiMode
    } else {
      frameWin.aiMode = aiMode.value
    }

    // metaFaceType（只要 aiMetaFace 會用到）
    const iframeMetaFaceType =
      (typeof frameWin.metaFaceType === 'string' && frameWin.metaFaceType)
        ? frameWin.metaFaceType
        : ''

    if (iframeMetaFaceType) {
      metaFaceType.value = iframeMetaFaceType
    } else {
      frameWin.metaFaceType = metaFaceType.value
    }

    // 如果你 camera.js 有提供這些方法就叫一下（沒提供就略過）
    if (typeof frameWin.applyModeUI === 'function') {
      try {
        frameWin.applyModeUI(currentMode.value)
      } catch (e) {
        console.debug('[OmniFaceTool] applyModeUI(mode) failed (ignored):', e)
      }
    }
  } catch (e) {
    console.debug('[OmniFaceTool] sync iframe state failed (ignored):', e)
  }

  // ----------------------------
  // 2) live frame listener
  // ----------------------------
  if (handler) {
    try {
      frameWin.removeEventListener('omni:live-frame', handler)
    } catch (e) {
      console.debug('[OmniFaceTool] remove omni:live-frame failed (ignored):', e)
    }
  }

  handler = (e) => {
    pending = e.detail
    if (rafId) return

    rafId = requestAnimationFrame(() => {
      liveFrame.value = pending
      pending = null
      rafId = 0
    })
  }

  try {
    frameWin.addEventListener('omni:live-frame', handler)
  } catch (e) {
    console.debug('[OmniFaceTool] add omni:live-frame failed (ignored):', e)
  }

  // ----------------------------
  // 3) optional: mode changed listener (from iframe)
  // ----------------------------
  if (modeHandler) {
    try {
      frameWin.removeEventListener('omni:mode-changed', modeHandler)
      syncCaptureState()
    } catch (e) {
      console.debug('[OmniFaceTool] remove omni:mode-changed failed (ignored):', e)
    }
  }

  modeHandler = (e) => {
    try {
      const d = e && e.detail ? e.detail : {}

      if (typeof d.mode === 'string' && d.mode) currentMode.value = d.mode
      if (typeof d.frMode === 'string' && d.frMode) frMode.value = d.frMode
      if (typeof d.aiMode === 'string' && d.aiMode) aiMode.value = d.aiMode
      if (typeof d.metaFaceType === 'string' && d.metaFaceType) metaFaceType.value = d.metaFaceType
    } catch (err) {
      console.debug('[OmniFaceTool] handle omni:mode-changed failed (ignored):', err)
    }
  }

  try {
    frameWin.addEventListener('omni:mode-changed', modeHandler)
  } catch (e) {
    console.debug('[OmniFaceTool] add omni:mode-changed failed (ignored):', e)
  }

  // ✅ 最後：iframe ready 後再吃 route 預設
  try { applyRoutePreset() } catch (e) {
    console.debug('[OmniFaceTool] applyRoutePreset ignored:', e)
  }

  syncGhostOverlay()
}

onBeforeUnmount(() => {
  document.body.style.overflow = ''
  window.removeEventListener('keydown', onKeydown)

  try {
    if (frameWin && handler) frameWin.removeEventListener('omni:live-frame', handler)
  } catch (e) {
    console.debug('[OmniFaceTool] cleanup ignored:', e)
  }
})

/** --------- UI meta --------- */
// const liveMeta = computed(() => {
//   // 你希望看得出來目前狀態：currentMode +（必要時）fr/ai
//   if (currentMode.value === 'FacialAgingScore') {
//     return `${currentMode.value} / frMode:${frMode.value}`
//   }
//   if (currentMode.value === 'facialAnalysis') {
//     return `${currentMode.value} / aiMode:${aiMode.value}`
//   }
//   return `${currentMode.value}`
// })

/** --------- chart data --------- */
async function renderChartOffscreenToPng(tab /* 'AU' | 'Emotion' */) {
  // 用你現成的資料組 series（不要改 viewTab，避免 UI 觸發重畫）
  const d = liveFrame.value
  if (!d) return null

  const prevTab = viewTab.value
  viewTab.value = tab

  // 用 computed 的 chartSeries / chartHeightPx 會跟著 tab 變，所以暫時切換，但不碰 DOM chart
  await nextTick()
  const series = chartSeries.value
  const height = Math.max(MIN_CHART_PX, (series.length || 0) * ROW_PX)

  // 還原 viewTab（避免 UI 跟著跳）
  viewTab.value = prevTab

  // 建立離螢幕容器
  const w = 360 // 右側 panel 寬度；要更準也可以取 panel DOM 寬
  const el = document.createElement('div')
  el.style.position = 'fixed'
  el.style.left = '-100000px'
  el.style.top = '0'
  el.style.width = `${w}px`
  el.style.height = `${height}px`
  el.style.background = 'transparent'
  document.body.appendChild(el)

  let inst = null
  try {
    inst = echarts.init(el, null, { renderer: 'canvas' })
    inst.setOption(buildOption(series), { notMerge: true })

    // pixelRatio 拉高，圖片更清楚
    const dataUrl = inst.getDataURL({
      type: 'png',
      pixelRatio: 2,
      backgroundColor: 'transparent'
    })

    return dataUrlToBlob(dataUrl)
  } finally {
    try { inst && inst.dispose() } catch (_) {/*ignore*/ }
    el.remove()
  }
}

function toSeriesAU (auValues) {
  if (!Array.isArray(auValues)) return []
  return auValues.map(x => ({
    key: String(x.au ?? x.key ?? '-'),
    value: Number(x.value ?? x.score ?? 0),
  }))
}

function toSeriesEmotion (emotionValues) {
  if (!emotionValues) return []

  if (Array.isArray(emotionValues)) {
    return emotionValues.map(x => ({
      key: String(x.emotion ?? x.key ?? '-'),
      value: Number(x.value ?? x.score ?? 0),
    }))
  }

  if (typeof emotionValues === 'object') {
    return Object.entries(emotionValues).map(([k, v]) => ({
      key: String(k),
      value: Number(v ?? 0),
    }))
  }

  return []
}

const EMOTION_ORDER = ['Happiness', 'Sadness', 'Anger', 'Surprise', 'Fear', 'Disgust']
const EMOTION_COLORS = {
  Happiness: '#FFD400',
  Sadness:   '#3FA9F5',
  Anger:     '#FF4D4F',
  Surprise:  '#FFA940',
  Fear:      '#9254DE',
  Disgust:   '#52C41A',
}
const AU_COLOR = '#4DA3FF'

function normalizeToPct(rawList) {
  const max = Math.max(1, ...rawList.map(x => x.value))
  const is01 = max <= 1.01
  return rawList.map(x => {
    const val = is01 ? x.value * 100 : x.value
    const pct = Math.max(0, Math.min(100, val))
    return { ...x, value: val, pct }
  })
}

/** --------- AU master (固定列) --------- */
const AU_MASTER = ref([])

function parseAuKey(key) {
  const m = /^AU(\d+)(?:\((L|R)\))?$/.exec(key)
  if (!m) return { n: Number.POSITIVE_INFINITY, side: '' }
  return { n: Number(m[1]), side: m[2] ?? '' }
}

function sortAuKeys(keys) {
  const sideRank = (s) => (s === 'L' ? 0 : s === 'R' ? 1 : 2)
  return [...keys].sort((a, b) => {
    const pa = parseAuKey(a)
    const pb = parseAuKey(b)
    if (pa.n !== pb.n) return pa.n - pb.n
    return sideRank(pa.side) - sideRank(pb.side)
  })
}

watch(liveFrame, (d) => {
  if (!d?.auValues) return
  const raw = toSeriesAU(d.auValues)
  const keys = raw.map(x => x.key).filter(k => /^AU\d+(\((L|R)\))?$/.test(k))
  if (!keys.length) return

  const set = new Set(AU_MASTER.value)
  let changed = false
  for (const k of keys) {
    if (!set.has(k)) { set.add(k); changed = true }
  }
  if (changed) AU_MASTER.value = sortAuKeys(Array.from(set))
})

const chartSeries = computed(() => {
  const d = liveFrame.value
  if (!d) return []

  if (viewTab.value === 'AU') {
    const raw = toSeriesAU(d.auValues)
    const map = new Map(raw.map(x => [x.key, Number(x.value ?? 0)]))
    const keys = AU_MASTER.value.length ? AU_MASTER.value : sortAuKeys(raw.map(x => x.key))
    const fixed = keys.map(key => ({ key, value: map.get(key) ?? 0, color: AU_COLOR }))
    return normalizeToPct(fixed)
  }

  const raw = toSeriesEmotion(d.emotionValues)
  const map = new Map(raw.map(x => [x.key, Number(x.value ?? 0)]))
  const fixed = EMOTION_ORDER.map(key => ({ key, value: map.get(key) ?? 0, color: EMOTION_COLORS[key] ?? '#fff' }))
  return normalizeToPct(fixed)
})

const ROW_PX = 34
const MIN_CHART_PX = 240
const chartHeightPx = computed(() => {
  const n = chartSeries.value.length || 0
  const h = Math.max(MIN_CHART_PX, n * ROW_PX)
  return `${h}px`
})


/** --------- ECharts lifecycle --------- */
let chart = null
let ro = null

function disposeChart() {
  try { ensureChart._cleanup && ensureChart._cleanup() } catch (_) {/*ignore*/ }
  ensureChart._cleanup = null
  try { if (ro) ro.disconnect() } catch (e) {/*ignore*/}
  try { if (chart) chart.dispose() } catch (e) {/*ignore*/}
  chart = null
  ro = null
}


function ensureChart() {
  if (chart || !chartEl.value) return

  chart = echarts.init(chartEl.value, null, { renderer: 'canvas' })

  // ----------------------------
  // 1) 正常的 series / bar 點擊（右邊 bar 跟數值）
  // ----------------------------
  chart.off('click')
  chart.on('click', (params) => {
    // bar / 右側數值：componentType = 'series'，值在 params.name
    const key = params?.name
    if (!key) return

    if (viewTab.value === 'AU' && /^AU\d+/.test(String(key))) {
      openGifModalByKey(key)
    } else if (viewTab.value === 'Emotion') {
      openGifModalByKey(key)
    }
  })

  const zr = chart.getZr()
  const root = zr.painter.getViewportRoot()

  // ----------------------------
  // 2) helpers：用「真實 grid rect」+「labels」來算你點到哪個 y label
  // ----------------------------
  function getGridRect() {
    // 取得真實 grid 區域（不是用 leftPad 猜）
    try {
      const grid = chart.getModel().getComponent('grid', 0)
      return grid.coordinateSystem.getRect() // {x,y,width,height}
    } catch (e) {
      return null
    }
  }

  function getLabels() {
    const opt = chart.getOption()
    return opt?.yAxis?.[0]?.data || []
  }

  function canOpenGif(key) {
    const k = String(key || '')
    if (viewTab.value === 'AU') return /^AU\d+/.test(k)
    if (viewTab.value === 'Emotion') {
      return ['Anger','Disgust','Fear','Happiness','Sadness','Surprise'].includes(k)
    }
    return false
  }

  // ----------------------------
  // 3) 超穩 tap：pointer capture + tap 判斷（解決「游標變手但點不動」）
  // ----------------------------
  const TAP_MOVE_PX = 10
  const TAP_MS = 450
  let tap = null

  function getZrXY(domEvent) {
    // DOM client -> ZRender 像素座標（避免縮放時 offsetX/Y 不準）
    const br = root.getBoundingClientRect()
    const sx = zr.getWidth() / br.width
    const sy = zr.getHeight() / br.height
    return {
      x: (domEvent.clientX - br.left) * sx,
      y: (domEvent.clientY - br.top) * sy,
    }
  }

  function pickKeyByDomEvent(domEvent) {
    const rect = getGridRect()
    const labels = getLabels()
    if (!rect || !labels.length) return ''

    const { x, y } = getZrXY(domEvent)

    // label 區：x < rect.x（grid 左邊界之前）
    const EPS = 8
    if (x >= rect.x + EPS) return ''

    // y 必須落在 grid 高度內
    if (y < rect.y - EPS || y > rect.y + rect.height + EPS) return ''

    const band = rect.height / labels.length
    const idxFromTop = Math.floor((y - rect.y) / band)

    // yAxis category 在很多情境是「由下往上」對應 labels
    const opt = chart.getOption()
    const inverse = !!(opt?.yAxis?.[0]?.inverse)

    // inverse=true 表示本來就反過來了；inverse=false 時我們用反向索引
    const idx = inverse
      ? idxFromTop
      : (labels.length - 1 - idxFromTop)

    return labels[idx] || ''
  }

  function isTap(domEvent) {
    if (!tap) return false
    const dt = Date.now() - tap.t
    if (dt > TAP_MS) return false
    const dx = domEvent.clientX - tap.cx
    const dy = domEvent.clientY - tap.cy
    return (dx * dx + dy * dy) <= (TAP_MOVE_PX * TAP_MOVE_PX)
  }

  // ✅ 先清掉舊的 listener（避免重複掛載造成不穩）
  if (ensureChart._cleanup) {
    try { ensureChart._cleanup() } catch (_) {/*ignore*/}
    ensureChart._cleanup = null
  }

  const onPointerDown = (e) => {
    const key = pickKeyByDomEvent(e)
    if (!key || !canOpenGif(key)) return

    // 命中 label 區：把它當「點擊」，不要被捲動吃掉
    e.preventDefault()
    e.stopPropagation()

    try { root.setPointerCapture(e.pointerId) } catch (_) {/*ignore*/}

    tap = { key, t: Date.now(), cx: e.clientX, cy: e.clientY, pid: e.pointerId }
  }

  const onPointerUp = (e) => {
    if (!tap) return
    if (tap.pid !== e.pointerId) return

    const key = tap.key
    const ok = isTap(e)
    tap = null

    try { root.releasePointerCapture(e.pointerId) } catch (_) {/*ignore*/}
    if (!ok) return

    openGifModalByKey(key)
  }

  const onPointerCancel = (e) => {
    if (tap && tap.pid === e.pointerId) tap = null
    try { root.releasePointerCapture(e.pointerId) } catch (_) {/*ignore*/}
  }

  const onMouseMove = (e) => {
    // 桌機游標提示：滑到 label 區才變手
    const key = pickKeyByDomEvent(e)
    zr.setCursorStyle(key && canOpenGif(key) ? 'pointer' : 'default')
  }

  const onGlobalOut = () => zr.setCursorStyle('default')

  root.addEventListener('pointerdown', onPointerDown, { passive: false })
  root.addEventListener('pointerup', onPointerUp, { passive: false })
  root.addEventListener('pointercancel', onPointerCancel, { passive: false })
  root.addEventListener('mousemove', onMouseMove, { passive: true })
  zr.on('globalout', onGlobalOut)

  // ✅ 記錄 cleanup：下次 ensureChart 或 disposeChart 時可以移除乾淨
  ensureChart._cleanup = () => {
    try { root.removeEventListener('pointerdown', onPointerDown) } catch (_) {/*ignore*/ }
    try { root.removeEventListener('pointerup', onPointerUp) } catch (_) {/*ignore*/ }
    try { root.removeEventListener('pointercancel', onPointerCancel) } catch (_) {/*ignore*/ }
    try { root.removeEventListener('mousemove', onMouseMove) } catch (_) {/*ignore*/ }
    try { zr.off('globalout', onGlobalOut) } catch (_) {/*ignore*/ }
  }

  // ----------------------------
  // 4) ResizeObserver（保留你原本的）
  // ----------------------------
  let resizeRaf = 0
  ro = new ResizeObserver(() => {
    if (resizeRaf) return
    resizeRaf = requestAnimationFrame(() => {
      resizeRaf = 0
      chart && chart.resize()
    })
  })
  ro.observe(chartEl.value)
}

function buildOption(series) {
  const reversed = [...series].reverse()
  const labels = reversed.map(x => x.key)
  const data = reversed.map(x => ({
    value: x.value,
    itemStyle: { color: x.color },
  }))

  const leftPad = viewTab.value === 'Emotion' ? 92 : 72

  return {
    animation: false,
    grid: { left: leftPad, right: 14, top: 10, bottom: 16, containLabel: false },
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(0,0,0,0.75)',
      borderWidth: 0,
      textStyle: { color: '#fff' },
      formatter: (p) => {
        const v = Number(p.value ?? 0)
        return `${p.name}<br/>${v.toFixed(2)}%`
      }
    },
    xAxis: {
      type: 'value',
      min: 0,
      max: 100,
      splitNumber: 5,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: 'rgba(255,255,255,0.65)', fontSize: 11 },
      splitLine: { lineStyle: { color: 'rgba(255,255,255,0.08)' } },
    },
    yAxis: {
      type: 'category',
      data: labels,
      triggerEvent: true,
      axisTick: { show: false },
      axisLine: { lineStyle: { color: 'rgba(255,255,255,0.18)' } },
      axisLabel: {
        triggerEvent: true,
        fontSize: 11,
        width: leftPad - 10,
        overflow: 'truncate',
        // ✅ 用 rich text 拼出「文字 + 小按鈕感」
        formatter: (val) => `{name|${val}}  {btn|ⓘ}`,
        rich: {
          name: { color: 'rgba(255,255,255,0.86)' },
          btn: {
            color: 'rgba(255,255,255,0.95)',
            backgroundColor: 'rgba(168, 85, 247, 0.25)',
            borderColor: 'rgba(168, 85, 247, 0.55)',
            borderWidth: 1,
            borderRadius: 6,
            padding: [1, 6, 1, 6],
          }
        }
      },
    },

    series: [
      {
        type: 'bar',
        data,
        barWidth: 14,
        itemStyle: { borderRadius: [0, 6, 6, 0] },
        label: {
          show: true,
          position: 'right',
          color: 'rgba(255,255,255,0.82)',
          fontSize: 11,
          formatter: (p) => `${Number(p.value ?? 0).toFixed(2)}%`,
        },
      }
    ],
  }
}

async function renderChart() {
  // 只有 FacialAssessment 且有資料才畫
  if (!showMetricsPanel.value || !liveFrame.value) return
  await nextTick()

  ensureChart()
  if (!chart) return

  try {
    chart.resize()
    chart.setOption(buildOption(chartSeries.value), { notMerge: false })
  } catch (e) {
    console.debug('[OmniFaceTool] setOption ignored:', e)
  }
}

// ✅ 面板被隱藏時，直接釋放 chart（避免 ResizeObserver 卡住 DOM）
watch(showMetricsPanel, (v) => {
  if (!v) {
    disposeChart()
  } else {
    // 回來再重畫
    renderChart()
  }
})

watch(chartSeries, () => { renderChart() }, { deep: true })
watch(viewTab, () => { renderChart() })
watch(liveFrame, (v) => { if (v) renderChart() })

onBeforeUnmount(() => {
  disposeChart()
})
</script>

<style scoped>
/* 修改 .wrap 與 .topbar 樣式 */
.wrap {
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  /* 導入主題色漸層，增加空間感 */
  background: radial-gradient(circle at 50% 10%, #1a103d 0%, #050508 100%);
}

.topbar {
  display: flex; 
  align-items: center; 
  gap: 12px;
  padding: 12px 24px;
  /* 磨砂玻璃效果 */
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border-bottom: 1px solid rgba(168, 85, 247, 0.2);
  z-index: 100;
}

.back {
  border: 1px solid rgba(255,255,255,0.2);
  background: rgba(255,255,255,0.06);
  padding: 6px 10px;
  border-radius: 10px;
  cursor: pointer;
  color: #ffffff;
}

.iconBtn.active {
  background: rgba(168, 85, 247, 0.4);
  border-color: rgba(168, 85, 247, 0.8);
  color: #fff;
}

/* 獨立左下方操作區 */
.bottom-left-controls {
  position: absolute;
  left: 20px;
  bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 50; /* 在 frame 內容之上 */
  pointer-events: auto; /* 讓其底下的子元素（按鈕）可被點擊 */
}

/* .ghost-overlay-img 樣式已移除，因為已移至 iframe 內部 */

.stacked-toggles {
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* 加一點視覺分界 */
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.stacked-btn {
  text-align: center;
  padding: 8px 12px;
  font-size: 13px;
  min-width: 60px; /* 寬度統一，才不會因為字長太小 */
  box-shadow: 0 4px 12px rgba(0,0,0,0.5); /* 稍微有點懸浮感 */
}

/* 尋找檔案中約第 880 行的 .title */
.title { 
  font-weight: 700; 
  /* 新增以下程式碼來達成漸層色 */
  background: linear-gradient(to right, #ffffff 30%, #a855f7 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;
}
.spacer { flex: 1; }

.pill {
  border: 1px solid rgba(255,255,255,0.15);
  background: rgba(255,255,255,0.06);
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  opacity: 0.9;
}

.content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: row;
  background: #000;
}

/* 左：iframe */
/* 左：iframe */
.frameWrap {
  flex: 1;
  min-width: 0;
  min-height: 0;
  background: #000;
  position: relative; /* ✅ 讓 overlay 可以絕對定位在上面 */
}

.frame {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
}

/* ✅ 浮動層：整層不吃點擊，只有按鈕可點 */
.liveOverlay{
  position: absolute;
  inset: 0;
  pointer-events: none;      /* ✅ 不擋 iframe */
  z-index: 20;

  display: flex;
  align-items: flex-end;
  justify-content: center;

  padding: 50px;
}

/* ✅ 浮動拍攝鍵：像相機快門 */
.fabCapture {
  pointer-events: auto;
  width: 72px;
  height: 72px;
  border-radius: 999px;
  cursor: pointer;
  /* 呼應主題的紫色邊框與呼吸效果 */
  border: 2px solid rgba(168, 85, 247, 0.6);
  background: rgba(168, 85, 247, 0.15);
  color: #fff;
  backdrop-filter: blur(10px);
  /* 預設就有微弱呼吸感 */
  animation: button-pulse 3s infinite ease-in-out;
  transition: all 0.3s ease;
}

/* 桌機預設：不顯示 icon */
.fabCapture .camIcon {
  display: none;
}

.fabCapture:hover {
  background: rgba(168, 85, 247, 0.3);
  box-shadow: 0 0 20px rgba(168, 85, 247, 0.4);
  transform: scale(1.05);
}

@keyframes button-pulse {
  0% { box-shadow: 0 0 0 0 rgba(168, 85, 247, 0.4); }
  70% { box-shadow: 0 0 0 15px rgba(168, 85, 247, 0); }
  100% { box-shadow: 0 0 0 0 rgba(168, 85, 247, 0); }
}

.fabCapture:active{
  transform: translateY(0px) scale(0.98);
}

/* ✅ 重拍狀態：變成紅色提示 */
.fabCapture.danger{
  border-color: rgba(255,80,80,0.85);
  background: rgba(255,80,80,0.18);
}
.fabCapture.danger:hover{
  background: rgba(255,80,80,0.24);
}

/* ✅ 手機：用 viewport 高度釘死，不吃父層 100% 的不確定性 */
@media (max-width: 899px) {
  .wrap {
    height: 100dvh;      /* ✅ 動態視窗高度：解決手機網址列伸縮造成的半截 */
    min-height: 100dvh;
  }

  .topbar {
    flex: 0 0 auto;
  }

  .content {
    flex: 1;
    min-height: 0;
  }

  .frameWrap {
    flex: 1;
    min-height: 0;
  }

  .frame {
    height: 100%;
  }

  /* ✅ 在「年輕化 / AI」模式，把上面工具列縮一點，畫面更大 */
  .topbar.compact {
    padding: 8px 12px;      /* 原本 12px 16px */
    gap: 8px;
  }

  .topbar.compact .iconBtn,
  .topbar.compact .back,
  .topbar.compact .metaSelect {
    padding: 5px 8px;
    font-size: 12px;
  }
  .content.full {
    /* 讓 iframe 更像全螢幕主角 */
    padding: 0;
  }
}



/* 右：panel（只在 FacialAssessment 才會出現） */
.panel {
  width: 360px;
  max-width: 42vw;
  min-width: 300px;

  border-left: 1px solid rgba(255,255,255,0.12);
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(8px);

  padding: 10px 12px 12px;
  overflow: hidden;

  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
  
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(20px);
  border-left: 1px solid rgba(168, 85, 247, 0.15);
}

.panelHead { display: flex; align-items: center; justify-content: space-between; }
.panelTitle { font-weight: 700; font-size: 14px; color: #fff;}

.seg {
  display: inline-flex;
  padding: 3px;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,0.14);
  background: rgba(255,255,255,0.06);
  gap: 4px;
}

.segBtn {
  padding: 7px 12px;
  border-radius: 999px;
  background: transparent;
  border: 0;
  color: #fff;
  cursor: pointer;
  font-size: 12px;
  opacity: 0.75;
  transition: opacity 160ms ease, background 160ms ease;
  white-space: nowrap;
}
.segBtn.active {
  /* 分段切換鈕選中時亮起主題色 */
  background: linear-gradient(135deg, #a855f7 0%, #7e22ce 100%);
  box-shadow: 0 2px 10px rgba(168, 85, 247, 0.3);
  opacity: 1;
}

/* chart container */
.echartsWrap {
  flex: 1;
  min-height: 0;
  border-radius: 12px;
  overflow: hidden;
  
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(168, 85, 247, 0.1);
  box-shadow: inset 0 0 20px rgba(0, 0, 0, 0.5);
}

.echartsScrollY {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}

.echartsInner { width: 100%; }
.echarts { width: 100%; height: 100%; }

.empty { opacity: 0.7; font-size: 12px; padding: 10px 0; color: #fff;}

/* ✅ 手機：面板變下面 */
@media (max-width: 899px) {
  .content { flex-direction: column; }
  .panel {
    width: auto;
    max-width: none;
    min-width: 0;
    border-left: 0;
    border-top: 1px solid rgba(255,255,255,0.12);
    height: 280px;
  }
}

.iconBtn {
  border: 1px solid rgba(255,255,255,0.18);
  background: rgba(255,255,255,0.06);
  color: #ffffff;
  padding: 6px 10px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 12px;
  opacity: 0.9;
}
.iconBtn:hover { opacity: 1; background: rgba(255,255,255,0.10); }
.mini{
  display: flex;
  align-items: center;
  gap: 10px;          /* 用 gap 排，不用 margin-left */
  flex-wrap: nowrap;  /* ✅ 關鍵：不換行 */
  min-width: 0;       /* 避免一些瀏覽器怪異撐寬 */
}

/* ✅ 手機：拍攝鍵 = 小長方形紫色圓角（含 photo_camera icon） */
@media (max-width: 899px) {
  .liveOverlay{
    inset: 0 !important;
    pointer-events: none;
    z-index: 20;

    display: flex;
    align-items: flex-end;
    justify-content: center;

    padding: 0 !important;   /* ✅ 確實貼到底（相對 frameWrap） */
  }

  .fabCapture {
    pointer-events: auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 12px;

    /* 維持長方形尺寸 */
    width: min(150px, 62vw) !important;
    height: 46px !important;
    border-radius: 12px !important;

    /* --- 玻璃感關鍵修改 --- */
    /* 1. 使用半透明漸層作為背景，保留原本的紫色調但更通透 */
    background: linear-gradient(
      135deg,
      rgba(109, 40, 217, 0.45) 0%,
      rgba(109, 40, 217, 0.25) 100%
    ) !important;

    /* 2. 加入一條細微的高光邊框，模擬玻璃邊緣 */
    border: 1px solid rgba(255, 255, 255, 0.18) !important;
    
    /* 3. 增強模糊度，背景越雜亂時效果越明顯 */
    backdrop-filter: blur(12px) !important;
    -webkit-backdrop-filter: blur(12px) !important;

    /* 4. 修改陰影，讓紫色光暈透出來 */
    box-shadow: 
      0 8px 32px 0 rgba(0, 0, 0, 0.37),
      inset 0 0 0 1px rgba(255, 255, 255, 0.1) !important;
    /* ---------------------- */

    color: #fff !important;
    font-size: 18px !important;
    font-weight: 800 !important;
    letter-spacing: .5px !important;
    opacity: 1 !important; /* 既然用了玻璃感漸層，就不需要整體調低透明度 */
    transform: none !important;

    /* 為了讓呼吸燈跟著按鈕 */
    animation: breathing 2.5s infinite ease-in-out;
  }

  /* 如果要維持之前的紫色呼吸燈效果 */
  @keyframes breathing {
    0%, 100% {
      box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37), 0 0 5px rgba(168, 85, 247, 0.2);
      border-color: rgba(255, 255, 255, 0.18);
    }
    50% {
      box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37), 0 0 20px rgba(168, 85, 247, 0.5);
      border-color: rgba(168, 85, 247, 0.5); /* 呼吸時邊框變紫 */
    }
  }

  /* Google Material icon */
  .fabCapture .camIcon{
    font-size: 18px;
    line-height: 1;
    opacity: 0.95;
  }

  .fabCapture:hover{
    background: #5b21b6 !important;
    opacity: 0.95;
  }

  .fabCapture:active{
    background: #4c1d95 !important;
    opacity: 1;
  }

  /* 重拍狀態：低調紫紅 */
  .fabCapture.danger{
    background: #a21caf !important;
  }

  .fabCapture.danger:hover{
    background: #86198f !important;
  }

  .fabCapture .camIcon {
    display: inline-block;
    font-size: 1.5em !important;
    line-height: 1;
    opacity: 0.80;
  }
}

.iconBtn:disabled{
  opacity: .35;
  cursor: not-allowed;
}

</style>

<!-- ✅ Teleport 的 modal：不要 scoped -->
<style>
.omni-modal-mask{
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  z-index: 9999;
  display: grid;
  place-items: center;
  padding: 20px;
}

.omni-modal{
  width: min(520px, 92vw);
  border-radius: 14px;
  border: 1px solid rgba(255,255,255,0.14);
  background: rgba(15,15,15,0.92);
  backdrop-filter: blur(10px);
  padding: 14px;
  box-shadow: 0 18px 60px rgba(0,0,0,0.55);
  color: #fff;
}

.omni-modal-title{
  font-weight: 800;
  font-size: 14px;
  margin-bottom: 12px;
}

.omni-modal-btns{
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}

.omni-modal-btns button{
  border: 1px solid rgba(255,255,255,0.18);
  background: rgba(255,255,255,0.06);
  color: inherit;
  padding: 10px 12px;
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
}
.omni-modal-btns button:hover{
  background: rgba(255,255,255,0.10);
}

.omni-modal-foot{
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
}

.omni-modal-foot button{
  border: 1px solid rgba(255,255,255,0.18);
  background: transparent;
  color: inherit;
  padding: 8px 12px;
  border-radius: 10px;
  cursor: pointer;
  opacity: 0.85;
}
.omni-modal-foot button:hover{
  opacity: 1;
  background: rgba(255,255,255,0.06);
}
.metaSelectWrap{
  display: inline-flex;
  align-items: center;
  margin-left: 0; /* 用 mini 的 gap 控制距離 */
  min-width: 0;
}

.metaSelect{
  height: 32px;                 /* 跟 iconBtn 視覺一致 */
  border: 1px solid rgba(255,255,255,0.18);
  background: rgba(255,255,255,0.06);
  color: inherit;
  padding: 6px 10px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 12px;
  opacity: 0.9;
  outline: none;
  max-width: 180px;             /* ✅ 防止太長把整排撐爆 */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}


.metaSelect:hover {
  opacity: 1;
  background: rgba(255,255,255,0.10);
}

.metaSelect option{
  background: #111;
  color: #fff;
}

.hiddenFile {
  display: none;
}

.hidden-ghost-input {
  display: none;
}

.camPanel{
  position: absolute;
  top: 52px;           /* 依你的 topbar 高度微調 */
  right: 16px;
  width: 280px;
  border-radius: 14px;
  border: 1px solid rgba(255,255,255,0.16);
  background: rgba(15,15,15,0.92);
  backdrop-filter: blur(10px);
  box-shadow: 0 18px 60px rgba(0,0,0,0.55);
  padding: 12px;
  z-index: 50;
}

.camRow{
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  min-width: 0;                 /* ✅ 保險 */
}

.camRow:first-child{ margin-top: 0; }
.camLabel{
  width: 42px;
  flex: 0 0 42px;               /* ✅ 固定寬度，不跟 select 搶空間 */
  font-size: 12px;
  opacity: .85;
  color: #fff;                  /* ✅ 白色 */
}


.camSelect{
  flex: 1;
  min-width: 0;                 /* ✅ 關鍵：允許在 flex 裡縮小，不然會撐爆 */
  width: 100%;
  max-width: 100%;              /* ✅ 防止超出 camPanel */
  height: 32px;

  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.18);
  background: rgba(255,255,255,0.06);
  color: #fff;                  /* ✅ 文字白色 */
  padding: 0 28px 0 10px;       /* ✅ 右邊留空避免箭頭蓋字 */
  outline: none;

  overflow: hidden;             /* ✅ 超長字不要撐出去 */
  text-overflow: ellipsis;      /* ✅ 省略號 */
  white-space: nowrap;
  box-sizing: border-box;
}

/* ✅ 下拉展開的選項文字也要白色（搭配深色底） */
.camSelect option{
  background: #111;             /* ✅ 很多瀏覽器 option 會吃系統色，這樣比較一致 */
  color: #fff;
}


.camCheck{ font-size: 12px; opacity: .9; display: inline-flex; gap: 8px; align-items: center; color: #fff;}

.camBtns{ justify-content: space-between; }
.camBtn{
  border: 1px solid rgba(255,255,255,0.18);
  background: rgba(255,255,255,0.06);
  color: inherit;
  padding: 8px 10px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 12px;
  opacity: 0.9;
  color: #fff;
}
.camBtn:hover{ opacity: 1; background: rgba(255,255,255,0.10); }

.iconBtn.danger{
  border-color: rgba(255,80,80,0.35);
  background: rgba(255,80,80,0.12);
}
.iconBtn.danger:hover{
  background: rgba(255,80,80,0.18);
}
</style>
